import * as React from 'react';
import { cn } from '@/lib/utils.ts';

/**
 * Wygląd skorupy karty.
 *
 * `dashed` to karta zepchnięta na drugi plan — kreskowana ramka na szarym tle,
 * bez cienia (np. zmiana bez wpływu na cenę w kaflu osoby).
 */
export type CardVariant = 'solid' | 'dashed';

interface CardProps extends React.ComponentPropsWithRef<'div'> {
  variant?: CardVariant;
  /** Karta w centrum uwagi — bursztynowa ramka (tryb edycji, otwarty szkic). */
  highlighted?: boolean;
}

const Card = ({
  variant = 'solid',
  highlighted = false,
  className,
  ...props
}: CardProps) => (
  <div
    className={cn(
      'rounded-[14px] border',
      variant === 'dashed'
        ? 'border-dashed border-neutral-300 bg-neutral-50'
        : cn(
            'bg-basic-white shadow-[0_1px_2px_rgba(18,20,26,0.04)]',
            highlighted ? 'border-accent-border' : 'border-neutral-200',
          ),
      className,
    )}
    {...props}
  />
);
Card.displayName = 'Card';

const CardHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col gap-1 border-b border-neutral-200 px-4 py-3',
      className,
    )}
    {...props}
  />
);
CardHeader.displayName = 'CardHeader';

const CardTitle = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) => (
  <h3
    className={cn(
      'text-xl font-bold leading-[120%] tracking-[0.02em]',
      className,
    )}
    {...props}
  />
);
CardTitle.displayName = 'CardTitle';

const CardDescription = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('text-sm text-neutral-500', className)} {...props} />
);
CardDescription.displayName = 'CardDescription';

const CardContent = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('p-4', className)} {...props} />
);
CardContent.displayName = 'CardContent';

const CardFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('flex items-center p-6 pt-0', className)} {...props} />
);
CardFooter.displayName = 'CardFooter';

/** Promień wewnętrznych nagłówków goni promień karty, stąd 13 px przy 14 px. */
const InnerCardHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="overflow-hidden rounded-t-[13px]">
    <div className="h-1 w-full bg-accent-600" />
    <div className={cn('bg-accent-100 p-3', className)} {...props} />
  </div>
);

const SecondaryInnerCardHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className="overflow-hidden rounded-t-[13px]">
    <div
      className={cn(
        'flex flex-row items-center justify-between bg-neutral-50 p-3 text-sm font-medium text-base-text',
        className,
      )}
      {...props}
    />
  </div>
);

export {
  Card,
  CardHeader,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
  InnerCardHeader,
  SecondaryInnerCardHeader,
};
